import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FaSearch, FaTimes, FaTrashAlt, FaHistory } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import "./SearchBar.scss";
import { searchMovies } from "../../Redux/actions/MovieThunk";
import { useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import {encryptId} from '../SecurityComponent/cryptoUtils';

const TMDB_KEY = '0623e0d232ae90274f2d77494ac4ce50';
const HISTORY_KEY = 'movie_search_history';
const HISTORY_MAX = 12;

const loadHistory = () => {
    try {
        const arr = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
        return Array.isArray(arr) ? arr.filter(s => typeof s === 'string') : [];
    } catch { return []; }
};

const SearchBar = () => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [trailerMovie, setTrailerMovie] = useState(null);
    const [trailerKey, setTrailerKey] = useState(null);
    const [trailerLoading, setTrailerLoading] = useState(false);
    const [focused, setFocused] = useState(false);
    const [history, setHistory] = useState(loadHistory);
    const [hot, setHot] = useState([]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const containerRef = useRef(null);

    useEffect(() => {
        if (query.trim() === "") {
            setResults([]);
            return;
        }
        const delayDebounce = setTimeout(() => {
            fetchSearchResults(query);
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [query]);

    // Nạp danh sách "Tìm kiếm hot"
    useEffect(() => {
        fetch('/movies-search-api.php?action=hot&limit=10')
            .then(r => r.json())
            .then(d => { if (d?.success && Array.isArray(d.hot)) setHot(d.hot); })
            .catch(() => {});
    }, []);

    // Đóng dropdown khi click ra ngoài
    useEffect(() => {
        const onDocClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setFocused(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const fetchSearchResults = async (keyword) => {
        try {
            setLoading(true);
            setError(null);
            const response = await dispatch(searchMovies({ keyword, size: 100 }));

            setResults(response.content || []);
        } catch (err) {
            setError("Lỗi khi tìm kiếm dữ liệu");
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const clearSearch = () => {
        setQuery("");
        setResults([]);
    };

    // ─── Lịch sử tìm kiếm (localStorage) ───────────────────────────
    const persistHistory = (arr) => {
        setHistory(arr);
        try { localStorage.setItem(HISTORY_KEY, JSON.stringify(arr)); } catch {}
    };

    const saveHistory = (kw) => {
        const k = (kw || '').trim();
        if (!k) return;
        const next = [k, ...history.filter(h => h.toLowerCase() !== k.toLowerCase())].slice(0, HISTORY_MAX);
        persistHistory(next);
    };

    const removeHistory = (kw, e) => {
        e?.stopPropagation();
        persistHistory(history.filter(h => h !== kw));
    };

    const clearHistory = (e) => {
        e?.stopPropagation();
        persistHistory([]);
    };

    const logSearch = (kw) => {
        const k = (kw || '').trim();
        if (k.length < 2) return;
        try {
            fetch('/movies-search-api.php?action=log&kw=' + encodeURIComponent(k)).catch(() => {});
        } catch {}
    };

    // Thực hiện tìm kiếm (Enter / click lịch sử / click hot)
    const commitSearch = (kw) => {
        const k = (kw || '').trim();
        if (!k) return;
        saveHistory(k);
        logSearch(k);
        setQuery("");
        setResults([]);
        setFocused(false);
        navigate(`/search/${encodeURIComponent(k)}`);
    };

    const openTrailer = async (movie) => {
        setTrailerMovie(movie);
        setTrailerKey(null);
        setTrailerLoading(true);
        setQuery("");
        setResults([]);
        try {
            const r = await fetch(`/movies-trailer-api.php?movieId=${movie.id}`);
            const d = await r.json();
            const url = d.data?.trailerUrl || movie.trailerUrl || '';
            const m = url.match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (m) { setTrailerKey(m[1]); setTrailerLoading(false); return; }
        } catch {}
        try {
            const res = await fetch(`https://api.themoviedb.org/3/search/movie?api_key=${TMDB_KEY}&query=${encodeURIComponent(movie.title)}`);
            const d = await res.json();
            if (d.results?.length) {
                const vr = await fetch(`https://api.themoviedb.org/3/movie/${d.results[0].id}/videos?api_key=${TMDB_KEY}`);
                const vd = await vr.json();
                const t = vd.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube') || vd.results?.find(v => v.site === 'YouTube');
                if (t) { setTrailerKey(t.key); setTrailerLoading(false); return; }
            }
        } catch {}
        setTrailerLoading(false);
    };

    const handleMovieClick = (movie) => {
        // Lưu lịch sử bằng từ khoá đang gõ (kết quả được chọn)
        if (query.trim()) { saveHistory(query.trim()); logSearch(query.trim()); }
        setFocused(false);
        if (movie.episodeCount === 0) {
            openTrailer(movie);
        } else {
            window.location.href = `/watch/${movie.slug}/${encryptId(1)}`;
        }
    };

    const formatDuration = (minutes) => {
        if (!minutes) return "Đang cập nhật";
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h > 0 ? `${h}h ` : ""}${m > 0 ? `${m} phút` : ""}`.trim();
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            commitSearch(query.trim());
        }
    };

    const showSuggest = focused && query.trim() === "" && (history.length > 0 || hot.length > 0);

    return (
        <>
        <div className="search-container" ref={containerRef}>
            <div className="search-box">
                <FaSearch className="search-icon" />
                <input
                    type="text"
                    placeholder="Tìm kiếm..."
                    value={query}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setFocused(true)}
                    onChange={(e) => setQuery(e.target.value)}
                />
                {query && <FaTimes className="clear-icon" onClick={clearSearch} />}
            </div>

            {loading && <div className="search-loading">Đang tìm kiếm...</div>}
            {error && <div className="search-error">{error}</div>}

            {/* Gợi ý: Lịch sử + Hot (khi ô trống & đang focus) */}
            {showSuggest && (
                <div className="search-suggest">
                    {history.length > 0 && (
                        <div className="ss-block">
                            <div className="ss-head">
                                <span className="ss-title">Lịch sử tìm kiếm</span>
                                <FaTrashAlt className="ss-clear" title="Xoá tất cả" onClick={clearHistory} />
                            </div>
                            {history.map((h, i) => (
                                <div className="ss-hist-item" key={'h' + i} onClick={() => commitSearch(h)}>
                                    <FaHistory className="ss-hist-ico" />
                                    <span className="ss-hist-text">{h}</span>
                                    <FaTimes className="ss-hist-del" title="Xoá" onClick={(e) => removeHistory(h, e)} />
                                </div>
                            ))}
                        </div>
                    )}

                    {hot.length > 0 && (
                        <div className="ss-block">
                            <div className="ss-head">
                                <span className="ss-title">Tìm kiếm hot</span>
                            </div>
                            {hot.map((k, i) => (
                                <div className="ss-hot-item" key={'k' + i} onClick={() => commitSearch(k)}>
                                    <span className={'ss-hot-rank' + (i < 3 ? ' top' : '')}>{i + 1}</span>
                                    <span className="ss-hot-text">{k}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {query.trim() !== "" && results.length > 0 && (
                <div className="search-results">
                    <div className="search-section-title">Danh sách phim</div>
                    {results.map((movie, index) => (
                        <div
                            className="search-item"
                            key={movie.id || index}
                            onClick={() => handleMovieClick(movie)}
                            onMouseEnter={() => setHoveredItem(index)}
                            onMouseLeave={() => setHoveredItem(null)}
                            style={{
                                backgroundColor: hoveredItem === index ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                                transform: hoveredItem === index ? 'translateY(-2px)' : 'none'
                            }}
                        >
                            <img
                                src={movie.posterUrl || '/default-poster.jpg'}
                                alt={movie.title}
                                style={{
                                    transform: hoveredItem === index ? 'scale(1.05)' : 'scale(1)'
                                }}
                            />
                            <div className="movie-info">
                                <div
                                    className="movie-title"
                                    style={{
                                        color: hoveredItem === index ? '#ff5e00' : '#e5e5e5'
                                    }}
                                >
                                    {movie.title}
                                </div>
                                <div className="movie-meta">
                                    {movie.episodeCount && `• T${movie.episodeCount} `}
                                    {movie.releaseYear && `• ${movie.releaseYear} `}
                                    • {formatDuration(movie.duration)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        {trailerMovie && ReactDOM.createPortal(
            <div style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.88)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
                 onClick={() => { setTrailerMovie(null); setTrailerKey(null); }}>
                <div style={{ position:'relative', width:'100%', maxWidth:900, background:'#0d0d14', borderRadius:10, overflow:'hidden', boxShadow:'0 24px 80px rgba(0,0,0,0.9)' }}
                     onClick={e => e.stopPropagation()}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px' }}>
                        <h3 style={{ margin:0, color:'#fff', fontSize:15, fontWeight:600 }}>{trailerMovie.title}</h3>
                        <button onClick={() => { setTrailerMovie(null); setTrailerKey(null); }}
                                style={{ background:'none', border:'none', color:'#fff', cursor:'pointer', padding:4 }}>
                            <IoClose size={20} />
                        </button>
                    </div>
                    <div style={{ width:'100%', aspectRatio:'16/9' }}>
                        {trailerLoading ? (
                            <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'#000' }}>
                                <div style={{ width:32, height:32, border:'3px solid rgba(255,255,255,0.2)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
                            </div>
                        ) : trailerKey ? (
                            <iframe src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&rel=0`}
                                    allow="autoplay; encrypted-media; fullscreen" allowFullScreen
                                    style={{ width:'100%', height:'100%', border:0 }} title={trailerMovie.title} />
                        ) : (
                            <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'#000', color:'#888' }}>
                                Không tìm thấy trailer.
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        )}
        </>
    );
};

export default SearchBar;
